import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Delete } from '@factorialco/f0-react/icons/app'
import { AnimatePresence, motion } from 'framer-motion'

import { observer } from 'mobx-react'

import DirectUploader from 'modules/trainings/components/Files/Uploaders/DirectUploader'
import VideoPlayer from 'modules/trainings/components/VideoPlayer'
import { FADE_ANIMATION } from 'modules/trainings/components/content/items/shared/sharedAnimations'
import { mediaOverlay } from 'modules/trainings/components/content/items/shared/styles.vanilla.css'
import { videoContainer } from 'modules/trainings/components/content/styles.vanilla.css'
import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'

/**
 * Props for ContentVideo component
 */
export type ContentVideoProps = {
  video: AttachedFile
  readOnly: boolean
  trainingId: string
  description: string
  onChange: (video: AttachedFile) => void
  onDelete: () => void
}

type AnimatedWrapperProps = {
  children: React.ReactNode
  animationKey: string
}

const AnimatedWrapper = ({ children, animationKey }: AnimatedWrapperProps) => (
  <motion.div key={animationKey} {...FADE_ANIMATION}>
    {children}
  </motion.div>
)

type VideoDisplayProps = {
  videoUrl: string
  readOnly: boolean
  onDelete: () => void
}

const VideoDisplay = ({ videoUrl, readOnly, onDelete }: VideoDisplayProps) => (
  <Box position="relative" className={videoContainer}>
    <VideoPlayer src={videoUrl} />
    {!readOnly && (
      <Box className={mediaOverlay} background="grey400">
        <F0Button size="md" variant="neutral" onClick={onDelete} icon={Delete} hideLabel />
      </Box>
    )}
  </Box>
)

type VideoUploaderProps = {
  trainingId: string
  description: string
  onChange: (video: AttachedFile) => void
  onDelete: () => void
}

const VideoUploader = ({ trainingId, description, onChange, onDelete }: VideoUploaderProps) => {
  const handleUpload = async (uploadData: { file: File; signedId?: string }) => {
    const newVideo = AttachedFile.fromUpload(uploadData.file, uploadData.signedId || '')
    onChange(newVideo)
  }

  return (
    <DirectUploader
      onUpload={handleUpload}
      onDelete={onDelete}
      description={description}
      fileTypes={['video']}
      resourceType="Trainings::Training"
      resourceId={trainingId}
    />
  )
}

export const ContentVideo = observer(
  ({ video, readOnly, trainingId, description, onChange, onDelete }: ContentVideoProps) => {
    const hasVideo = !!video.url
    const shouldShowUploader = !hasVideo && !readOnly && trainingId

    return (
      <Box width="full">
        <AnimatePresence mode="wait">
          {hasVideo && (
            <AnimatedWrapper animationKey="video">
              <VideoDisplay videoUrl={video.url} readOnly={readOnly} onDelete={onDelete} />
            </AnimatedWrapper>
          )}

          {shouldShowUploader && (
            <AnimatedWrapper animationKey="uploader">
              <VideoUploader
                trainingId={trainingId}
                description={description}
                onChange={onChange}
                onDelete={onDelete}
              />
            </AnimatedWrapper>
          )}
        </AnimatePresence>
      </Box>
    )
  }
)
